import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { marginReportService } from '../../services/marginReportService';
import type { InventoryValueResponse } from '../../types/report';
import { ReportDateRangeControls } from './ReportDateRangeControls';
import { useReportDateRange } from './useReportDateRange';

/**
 * Рядок звірки. `signed` показує знак явно, бо тут важливо не «скільки», а «зайшло чи вийшло».
 */
function InventoryRow({ label, cost, quantity, bold, signed, highlighted }: {
  label: string;
  cost: number;
  quantity?: number;
  bold?: boolean;
  signed?: boolean;
  highlighted?: boolean;
}) {
  const sign = (value: number) => (signed ? (value >= 0 ? '+' : '−') : '');
  const money = signed ? `${sign(cost)}$${Math.abs(cost).toFixed(2)}` : `$${cost.toFixed(2)}`;
  const pieces = quantity === undefined
    ? '—'
    : signed ? `${sign(quantity)}${Math.abs(quantity)} шт` : `${quantity} шт`;

  return (
    <Box
      component="tr"
      sx={{
        backgroundColor: highlighted ? 'warning.light' : undefined,
        borderTop: bold ? '2px solid' : undefined,
        borderColor: 'divider',
      }}
    >
      <Box component="td" sx={{ p: 1, fontWeight: bold ? 700 : 400 }}>{label}</Box>
      <Box component="td" sx={{ p: 1, textAlign: 'right', fontWeight: bold ? 700 : 400 }}>{money}</Box>
      <Box component="td" sx={{ p: 1, textAlign: 'right', color: 'text.secondary' }}>{pieces}</Box>
    </Box>
  );
}

export default function InventoryValueReportPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = Number(id);

  const { preset, from, to, setFrom, setTo, handlePresetChange } = useReportDateRange();

  const [inventory, setInventory] = useState<InventoryValueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;
    let isCancelled = false;

    setLoading(true);
    setError(null);

    marginReportService.getInventoryValue(companyId, from, to)
      .then(response => {
        if (!isCancelled) setInventory(response);
      })
      .catch(err => {
        console.error('Failed to load inventory value report:', err);
        if (!isCancelled) setError('Не вдалося завантажити звірку вартості складу');
      })
      .finally(() => {
        if (!isCancelled) setLoading(false);
      });

    return () => { isCancelled = true; };
  }, [companyId, from, to]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}/reports`)}
          size="small"
        >
          Назад
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Вартість складу
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Відповідь на питання «я витратив гроші, а маржа їх не показує — де вони?». Вони на
        складі. Тут видно, скільки грошей туди зайшло за період і скільки вийшло разом із
        проданим товаром.
      </Alert>

      <ReportDateRangeControls
        preset={preset}
        onPresetChange={handlePresetChange}
        from={from}
        to={to}
        onFromChange={setFrom}
        onToChange={setTo}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : inventory && (
        <Paper sx={{ overflow: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <Box component="thead">
              <Box component="tr">
                {['', 'Сума', 'Кількість'].map(header => (
                  <Box
                    component="th"
                    key={header}
                    sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider', '&:first-of-type': { textAlign: 'left' } }}
                  >
                    {header}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              <InventoryRow
                label="На початок періоду"
                cost={inventory.openingCost}
                quantity={inventory.openingQuantity}
                bold
              />
              <InventoryRow
                label="Закупівлі"
                cost={inventory.purchases}
                quantity={inventory.purchasesQuantity}
                signed
              />
              <InventoryRow
                label="Собівартість проданого"
                cost={-inventory.costOfSales}
                quantity={-inventory.costOfSalesQuantity}
                signed
              />
              <InventoryRow
                label="На кінець періоду"
                cost={inventory.closingCost}
                quantity={inventory.closingQuantity}
                bold
              />
              <Box component="tr">
                <Box component="td" sx={{ p: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
                  Середня собівартість на кінець
                </Box>
                <Box component="td" sx={{ p: 1, textAlign: 'right', color: 'text.secondary', fontSize: '0.85rem' }}>
                  {inventory.closingQuantity > 0
                    ? `$${(inventory.closingCost / inventory.closingQuantity).toFixed(2)} / шт`
                    : '—'}
                </Box>
                <Box component="td" />
              </Box>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
